import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import type React from 'react';

import type * as api from '@gitbook/api-client';

import { apiQuery } from '../../../state';
import { UserAvatarStack } from '../../Users';
import { ChangeRequestParticipantsList } from '../ChangeRequestParticipantsList';
import { ChangeRequestReviewersBadges } from '../ChangeRequestReviewersBadges';
import { ChangeRequestReviewersList } from '../ChangeRequestReviewersList';
import {
    ChangeRequestSidePanelCollapsible,
    useChangeRequestSidePanelCollapsed,
} from './ChangeRequestSidePanelCollapsible';

type ChangeRequestDetailsProps = {
    changeRequest: api.ChangeRequest;
    space: api.Space;
};

export function ChangeRequestDetails(props: ChangeRequestDetailsProps) {
    const { changeRequest, space } = props;

    return (
        <ChangeRequestSidePanelCollapsible
            width={300}
            direction="right"
            className="@4xl:block hidden divide-y border-l"
        >
            {changeRequest.status !== 'draft' ? (
                <SidebarSection>
                    <ChangeRequestReviewersList changeRequest={changeRequest} space={space} />
                </SidebarSection>
            ) : null}
            <SidebarSection>
                <ChangeRequestParticipantsList changeRequest={changeRequest} />
            </SidebarSection>
        </ChangeRequestSidePanelCollapsible>
    );
}

export function ChangeRequestDetailsResponsive(props: ChangeRequestDetailsProps) {
    const { changeRequest, space } = props;
    const isCollapsed = useChangeRequestSidePanelCollapsed();

    return (
        <div
            className={clsx(
                'flex flex-row flex-wrap items-center gap-x-6 gap-y-3',
                isCollapsed ? '' : '@4xl:hidden'
            )}
        >
            <ChangeRequestReviewersBadges changeRequest={changeRequest} space={space} />
            <ChangeRequestParticipantsResponsive changeRequest={changeRequest} />
        </div>
    );
}

function ChangeRequestParticipantsResponsive(props: { changeRequest: api.ChangeRequest }) {
    const { changeRequest } = props;

    const { data: edits } = useSuspenseQuery(
        apiQuery('getContributorsByChangeRequestId', {
            spaceId: changeRequest.space,
            changeRequestId: changeRequest.id,
        })
    );

    const users = edits.items.map((item) => item.user);

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex h-5 items-center text-muted text-ui-small">
                <span>Participants</span>
            </div>
            <div className="flex items-center">
                <UserAvatarStack users={users.map((user) => ({ id: user.id }))} size="sm" />
            </div>
        </div>
    );
}

function SidebarSection(props: { children: React.ReactNode }) {
    return <div className="p-4">{props.children}</div>;
}
